import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuArrowUpRight, LuBookOpen, LuLibrary, LuPlus, LuSearch, LuUserCheck, LuUsers } from 'react-icons/lu';
import AuthContext from '../shared/AuthContext';
import LearnSubscribedNotebooks from '../learn/LearnSubscribedNotebooks';
import NotebookAvatar from '../../pages/learn/NotebookAvatar';
import { decodeHtmlEntities } from '../../utils/formatStructuredAnswer';
import {
  catalogQuestionCount,
  catalogReaderCount,
  catalogSubscriberTotal,
  formatEngagementCount,
  normalizeCatalogItems,
} from '../../utils/catalogEngagement';
import './LibraryReadListPanel.css';

/** Catalog read list for Library — shown on Learn. */
export default function LibraryReadListPanel({ onChanged }) {
  const { apiClient } = useContext(AuthContext);
  const [notebooks, setNotebooks] = useState([]);
  const [readListEntries, setReadListEntries] = useState([]);
  const [subscribedPublic, setSubscribedPublic] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const applyReadListPayload = (data) => {
    const topics = Array.isArray(data?.notebooks) ? data.notebooks : [];
    setNotebooks(topics);
    const entries = Array.isArray(data?.readListEntries) && data.readListEntries.length > 0
      ? data.readListEntries
      : topics.map((topic) => ({ topic, source: 'MANUAL' }));
    setReadListEntries(entries);
    setSubscribedPublic(
      Array.isArray(data?.subscribedPublicNotebooks) ? data.subscribedPublicNotebooks : [],
    );
    setCatalog(Array.isArray(data?.catalog) ? data.catalog : []);
    setCatalogItems(normalizeCatalogItems(data));
  };

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const [readListRes, publicRes] = await Promise.all([
        apiClient.get('/api/v1/library/read-list'),
        apiClient.get('/api/v1/learn/notebooks/public', { params: { limit: 80 } }).catch(() => ({ data: [] })),
      ]);
      const merged = {
        ...readListRes.data,
        publicNotebooks: publicRes.data?.length
          ? publicRes.data
          : readListRes.data?.publicNotebooks,
      };
      applyReadListPayload(merged);
    } catch {
      setLoadError(true);
      setStatus('Could not load your library Q&A.');
    }
  }, [apiClient]);

  useEffect(() => {
    load();
  }, [load]);

  const inReadList = useMemo(
    () => new Set(notebooks.map((n) => n.toLowerCase())),
    [notebooks],
  );

  const catalogAvailable = useMemo(
    () => catalogItems.filter((item) => !inReadList.has((item?.topic || '').toLowerCase())),
    [catalogItems, inReadList],
  );

  const catalogFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalogAvailable.filter((item) => {
      const topic = item?.topic || '';
      if (!q) return true;
      const desc = (item.description || '').toLowerCase();
      return topic.toLowerCase().includes(q) || desc.includes(q);
    });
  }, [catalogAvailable, search]);

  const patch = async (body, successMessage) => {
    const beforeCount = notebooks.length;
    setBusy(true);
    setStatus('');
    try {
      const res = await apiClient.patch('/api/v1/library/read-list', body);
      const after = Array.isArray(res.data?.notebooks) ? res.data.notebooks : [];
      applyReadListPayload(res.data);
      onChanged?.();
      setLoadError(false);
      const added = body.add?.length && after.length > beforeCount;
      const removed = body.remove?.length && after.length < beforeCount;
      if (successMessage) {
        setStatus(successMessage);
      } else if (added) {
        setStatus(`Subscribed to “${body.add[0]}”.`);
      } else if (removed) {
        setStatus('Unsubscribed.');
      } else if (body.add?.length) {
        setStatus('Could not subscribe — topic may not be in the catalog.');
      } else {
        setStatus('Library list updated.');
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message;
      setStatus(msg ? `Update failed: ${msg}` : 'Update failed. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const subscribeTopic = (topic) => patch({ add: [topic], remove: [] });
  const unsubscribeTopic = (topic) => patch({ add: [], remove: [topic] });

  const unsubscribePublic = async (notebook) => {
    setBusy(true);
    setStatus('');
    try {
      await apiClient.delete(`/api/v1/learn/notebooks/${notebook.id}/subscribe`);
      await load();
      onChanged?.();
      const label = decodeHtmlEntities(notebook.title || 'notebook');
      setStatus(`Unsubscribed from “${label}”.`);
    } catch {
      setStatus('Could not update subscription.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      id="learn-library"
      className="hub-library-catalog library-read-panel library-read-panel--learn"
      aria-labelledby="catalog-showcase-title"
    >
      <div className="library-read-unified-card">
      <div className="catalog-showcase" aria-labelledby="catalog-showcase-title">
        <div className="catalog-showcase-hero catalog-showcase-hero--split">
          <div className="catalog-showcase-hero-top">
            <div className="catalog-showcase-hero-main">
              <span className="catalog-showcase-icon" aria-hidden>
                <LuLibrary />
              </span>
              <div className="catalog-showcase-copy">
                <p className="catalog-showcase-eyebrow">Public Q&amp;A catalog</p>
                <h3 id="catalog-showcase-title" className="catalog-showcase-title">
                  Browse &amp; subscribe to shared topics
                </h3>
              </div>
            </div>
            <dl
              className="learn-page-stats learn-page-stats--hero catalog-showcase-metrics catalog-showcase-metrics--right"
              aria-live="polite"
            >
              <div>
                <dt>{search.trim() ? 'Matching' : 'Available'}</dt>
                <dd>{catalogFiltered.length}</dd>
              </div>
              <div>
                <dt>In catalog</dt>
                <dd>{catalogAvailable.length}</dd>
              </div>
            </dl>
          </div>
          <div className="catalog-showcase-lead-row">
            <p className="catalog-showcase-lead">
              Discover Q&amp;A topics other learners published. Preview any topic, subscribe once, then read
              and practice from your Library — no copy-paste setup.
            </p>
            <ol className="catalog-showcase-flow catalog-showcase-flow--inline" aria-label="How catalog subscription works">
              <li><span>Preview</span> sample questions</li>
              <li><span>Subscribe</span> to add to Library</li>
              <li><span>Read</span> in <Link to="/library">Library</Link></li>
            </ol>
          </div>
        </div>

        <div className="catalog-showcase-toolbar">
          <label className="catalog-showcase-search">
            <LuSearch className="catalog-showcase-search-icon" aria-hidden />
            <input
              type="search"
              className="library-read-search"
              placeholder="Search public topics…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search catalog topics"
            />
          </label>
          <Link to="/hub/notebooks/new" className="sh-block-link library-read-custom-btn learn-btn-with-icon">
            <LuPlus aria-hidden />
            Create Q&amp;A
          </Link>
        </div>

        <ul className="library-read-catalog hub-library-catalog-list library-read-catalog-list catalog-showcase-grid">
          {catalogFiltered.slice(0, 80).map((item) => {
            const topic = item.topic;
            const title = decodeHtmlEntities(topic);
            const desc = decodeHtmlEntities(item.description || '').trim();
            const subscribers = catalogSubscriberTotal(item);
            const reading = catalogReaderCount(item);
            const questions = catalogQuestionCount(item);
            const previewTo = item.learnNotebookId
              ? `/hub/notebooks/${item.learnNotebookId}`
              : `/library?notebook=${encodeURIComponent(topic)}&preview=1`;
            return (
              <li key={topic} className="library-read-catalog-row">
                <div className="library-read-catalog-main">
                  <div className="library-read-catalog-avatar" aria-hidden>
                    <NotebookAvatar size="sm" />
                  </div>
                  <div className="library-read-catalog-copy">
                    <span className="catalog-showcase-card-badge">Public</span>
                    <span className="library-read-catalog-title">{title}</span>
                    {desc ? (
                      <p className="library-read-catalog-desc">{desc}</p>
                    ) : (
                      <p className="library-read-catalog-desc library-read-catalog-desc--muted">
                        Public topic · tap Preview to sample questions
                      </p>
                    )}
                    <div className="library-read-catalog-stats" aria-label="Topic stats">
                      <span className="library-read-catalog-stat-chip">
                        <LuUsers aria-hidden />
                        <strong>{formatEngagementCount(subscribers)}</strong>
                        <span>subscribers</span>
                      </span>
                      <span className="library-read-catalog-stat-chip">
                        <LuUserCheck aria-hidden />
                        <strong>{formatEngagementCount(reading)}</strong>
                        <span>readers</span>
                      </span>
                      <span className="library-read-catalog-stat-chip">
                        <LuBookOpen aria-hidden />
                        <strong>{formatEngagementCount(questions)}</strong>
                        <span>questions</span>
                      </span>
                    </div>
                  </div>
                </div>
                <div className="library-read-catalog-actions">
                  <Link to={previewTo} className="library-read-preview-btn">
                    Preview
                    <LuArrowUpRight aria-hidden />
                  </Link>
                  <button
                    type="button"
                    className="library-read-subscribe-btn"
                    disabled={busy || loadError}
                    onClick={() => subscribeTopic(topic)}
                  >
                    Subscribe
                  </button>
                </div>
              </li>
            );
          })}
          {catalogFiltered.length === 0 && !loadError && (
            <li className="library-read-catalog-empty">
              <LuBookOpen className="library-read-catalog-empty-icon" aria-hidden />
              <p className="library-read-catalog-empty-title">
                {catalog.length === 0 ? 'Nothing in the catalog yet' : 'No topics match'}
              </p>
              <p className="library-read-catalog-empty-text">
                {catalog.length === 0
                  ? 'Check back later or create your own topic to share.'
                  : search.trim()
                    ? 'Try a different search term, or clear the filter.'
                    : 'You may already subscribe to everything listed here.'}
              </p>
              {search.trim() ? (
                <button
                  type="button"
                  className="library-read-catalog-empty-action"
                  onClick={() => setSearch('')}
                >
                  Clear search
                </button>
              ) : null}
            </li>
          )}
          {loadError && (
            <li className="library-read-catalog-empty">
              Could not load catalog.{' '}
              <button type="button" className="library-read-retry" onClick={load}>
                Retry
              </button>
            </li>
          )}
        </ul>

        <p className="catalog-showcase-footnote">
          Preview and subscribe to public topics — they appear in your{' '}
          <Link to="/library">Library</Link>. Manage{' '}
          <Link to="#learn-subscriptions">your subscriptions</Link> in the section below.
        </p>
      </div>

      <LearnSubscribedNotebooks
        readListEntries={readListEntries}
        publicNotebooks={subscribedPublic}
        busy={busy}
        onUnsubscribeCatalog={unsubscribeTopic}
        onUnsubscribePublic={unsubscribePublic}
      />

      </div>

      {status && (
        <p className={`library-read-status${loadError ? ' library-read-status--error' : ''}`} role="status">
          {status}
        </p>
      )}
    </section>
  );
}
